import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MessageSquare, Sparkles, Lightbulb, AlertTriangle, Quote, Instagram, Zap, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { runToolJob, TOOL_ETA } from "@/lib/toolJobs";
import JobProgress from "@/components/app/JobProgress";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import { useBrandDnaSummary } from "@/lib/useBrandDna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommentGoldminePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [platform, setPlatform] = useState(searchParams.get("platform") === "tiktok" ? "tiktok" : "instagram");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(null);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const { summary: dna } = useBrandDnaSummary();

  useEffect(() => {
    const p = searchParams.get("platform");
    if (p === "tiktok" || p === "instagram") setPlatform(p);
  }, [searchParams]);

  // Re-open a past run via ?jobId=...
  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (!jobId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tools/jobs/${jobId}`);
        if (data.status === "completed" && data.result) {
          setResult(data.result);
          setJobId(jobId);
          if (data.input?.url) setUrl(data.input.url);
        } else if (data.status === "failed") {
          toast.error(data.error || "Diese Analyse ist fehlgeschlagen");
        } else if (data.status === "running") {
          toast.info("Analyse läuft noch — bitte gleich nochmal probieren");
        }
      } catch (_) { /* silent */ }
    })();
  }, [searchParams]);

  const run = async () => {
    if (!url.trim()) return;
    setLoading(true); setResult(null); setTick(null);
    try {
      const data = await runToolJob("post", "/tools/comment-goldmine", { url: url.trim() }, {
        etaSec: TOOL_ETA["comment-goldmine"],
        onTick: setTick,
      });
      setResult(data);
      if (data?.__jobId) setJobId(data.__jobId);
      toast.success(`${data.scraped?.comments?.length || 0} Kommentare ausgewertet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Comment-Goldmine fehlgeschlagen");
    } finally { setLoading(false); setTick(null); }
  };

  const ai = result?.ai_analysis || {};
  const cs = result?.scraped?.comments || [];

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></Button>
          <MessageSquare className="w-5 h-5 text-cyan-400" />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Comment Goldmine</h1>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">{platform}</span>
        </div>

        {!result && (
          <div className="space-y-4">
            {dna && <BrandDnaCard summary={dna} variant="hint" />}
            <div className="flex gap-2">
              {[{ id: "instagram", label: "Instagram", icon: Instagram }, { id: "tiktok", label: "TikTok", icon: Zap }].map(p => (
                <button key={p.id} onClick={() => setPlatform(p.id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-medium transition-all ${platform === p.id ? "border-cyan-500 bg-cyan-500/10 text-cyan-400" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
                  <p.icon className="w-3.5 h-3.5" />{p.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-zinc-400">Füge die URL eines viralen Posts/Reels/Videos ein (deins oder Konkurrent). Wir scrapen die Top-Kommentare und die KI extrahiert Audience-Pains, Wunsch-Themen, FAQs und konkrete Content-Ideen aus der echten Sprache deiner Audience.</p>
            <Input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && run()} placeholder={platform === "tiktok" ? "https://www.tiktok.com/@user/video/..." : "https://www.instagram.com/p/... oder /reel/..."} className="bg-zinc-900 border-zinc-800 text-white h-11" data-testid="comments-url-input" />
            <Button onClick={run} disabled={loading || !url.trim()} className="w-full bg-gradient-to-r from-cyan-500 to-lime-400 hover:from-cyan-400 hover:to-lime-300 text-white h-11" data-testid="run-comments-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kommentare werden analysiert...</> : <><Sparkles className="w-4 h-4 mr-2" />Goldmine öffnen</>}
            </Button>
            {loading && <JobProgress tick={tick} label="Comment Goldmine läuft…" />}
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <p className="text-xs text-zinc-500 mb-1">Analysiert: {cs.length} Kommentare</p>
              <p className="text-sm text-zinc-200">{ai.summary}</p>
            </div>

            {ai.audience_pains?.length > 0 && (
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <p className="text-sm font-semibold text-amber-300 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Audience-Pains</p>
                <div className="space-y-3">{ai.audience_pains.map((p, i) => (
                  <div key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                    <p className="text-sm text-amber-200 font-medium">{p.pain}</p>
                    {p.evidence && <p className="text-[11px] text-zinc-500 mt-1 italic flex gap-1"><Quote className="w-3 h-3 mt-0.5" />{p.evidence}</p>}
                    {p.post_idea && <p className="text-xs text-cyan-400 mt-1.5">→ {p.post_idea}</p>}
                  </div>
                ))}</div>
              </div>
            )}

            {ai.wish_topics?.length > 0 && (
              <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20">
                <p className="text-sm font-semibold text-cyan-300 mb-2">Wunsch-Themen</p>
                <div className="space-y-3">{ai.wish_topics.map((t, i) => (
                  <div key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                    <p className="text-sm text-cyan-200 font-medium">{t.topic}</p>
                    {t.evidence && <p className="text-[11px] text-zinc-500 mt-1 italic">"{t.evidence}"</p>}
                    {t.post_idea && <p className="text-xs text-cyan-400 mt-1.5">→ {t.post_idea}</p>}
                  </div>
                ))}</div>
              </div>
            )}

            {ai.faq_for_stories?.length > 0 && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <p className="text-sm font-semibold text-white mb-2">FAQs für Stories</p>
                <div className="space-y-2">{ai.faq_for_stories.map((f, i) => (
                  <div key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                    <p className="text-xs font-medium text-cyan-400">Q: {f.question}</p>
                    <p className="text-xs text-zinc-300 mt-1">A: {f.short_answer}</p>
                  </div>
                ))}</div>
              </div>
            )}

            {ai.audience_language?.length > 0 && (
              <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                <p className="text-sm font-semibold text-emerald-300 mb-2">Audience-Sprache (1:1 für Captions klauen)</p>
                <div className="flex flex-wrap gap-1.5">{ai.audience_language.map((w, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">"{w}"</span>
                ))}</div>
              </div>
            )}

            {ai.next_post_ideas?.length > 0 && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-lime-400/5 border border-cyan-500/30">
                <p className="text-sm font-semibold text-cyan-300 mb-2 flex items-center gap-2"><Lightbulb className="w-4 h-4" />Deine 5 nächsten Posts</p>
                <ol className="space-y-2">{ai.next_post_ideas.map((idea, i) => (
                  <li key={i} className="text-xs text-zinc-200 flex gap-2"><span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span><span>{idea}</span></li>
                ))}</ol>
              </div>
            )}

            {ai.negative_signals?.length > 0 && (
              <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                <p className="text-xs font-medium text-red-400 mb-1">Vorsicht — was sie NICHT mögen:</p>
                <ul className="text-xs text-red-200/80 space-y-0.5">{ai.negative_signals.map((n, i) => <li key={i}>▸ {n}</li>)}</ul>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              {jobId && (
                <Button
                  asChild
                  className="flex-1 bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25"
                  data-testid="goldmine-download-pdf-btn"
                >
                  <a href={`${API}/tools/jobs/${jobId}/pdf`} target="_blank" rel="noreferrer">
                    <FileDown className="w-4 h-4 mr-2" /> Als PDF speichern
                  </a>
                </Button>
              )}
              <Button onClick={() => { setResult(null); setJobId(null); setUrl(""); }} variant="outline" className="flex-1 border-zinc-700 text-zinc-400">Anderen Post analysieren</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
